import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { ar } from 'date-fns/locale';
import {
    Box,
    Container,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Card,
    CardContent,
    Chip,
    useTheme,
    alpha,
    IconButton,
    Tooltip,
    Avatar
} from '@mui/material';
import {
    Add,
    Edit,
    Delete,
    Close,
    Event as EventIcon,
    WhatsApp,
    Telegram,
    Instagram,
    Facebook,
    Schedule,
    CheckCircle
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../config';
import CalendarToolbar from '../components/CalendarToolbar';
import { useNavigate } from 'react-router-dom';

// Import basic styles but override them below
import 'react-big-calendar/lib/css/react-big-calendar.css';
import styled from '@emotion/styled';

const locales = {
    'ar': ar,
};

const localizer = dateFnsLocalizer({
    format,
    parse,
    startOfWeek,
    getDay,
    locales,
});

// Custom Styled Component for Calendar Wrapper to override RBC default styles
const CalendarWrapper = styled(Box)(({ theme }) => ({
    height: '75vh',
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius * 2,
    boxShadow: theme.shadows[3],
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    '& .rbc-calendar': {
        fontFamily: theme.typography.fontFamily,
        color: theme.palette.text.primary,
        direction: 'rtl',
    },
    '& .rbc-header': {
        padding: '16px 0',
        fontWeight: 700,
        color: theme.palette.text.primary,
        borderBottom: `2px solid ${theme.palette.divider}`,
        backgroundColor: alpha(theme.palette.primary.main, 0.03),
        fontSize: '0.95rem',
    },
    '& .rbc-month-view': {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2),
    },
    '& .rbc-day-bg': {
        '& + .rbc-day-bg': {
            borderLeft: `1px solid ${theme.palette.divider}`,
        },
    },
    '& .rbc-off-range-bg': {
        backgroundColor: alpha(theme.palette.action.disabledBackground, 0.3),
    },
    '& .rbc-date-cell': {
        padding: '8px',
        color: theme.palette.text.secondary,
        fontSize: '0.9rem',
        fontWeight: 500,
    },
    '& .rbc-event': {
        borderRadius: '6px',
        boxShadow: theme.shadows[1],
        border: 'none',
        padding: '4px 6px',
        margin: '2px 4px',
        transition: 'transform 0.2s',
        '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: theme.shadows[3],
            zIndex: 10,
        }
    },
    '& .rbc-today': {
        backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    '& .rbc-time-view': {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2),
    },
    '& .rbc-time-header': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .rbc-timeslot-group': {
        borderBottom: `1px solid ${theme.palette.divider}`,
    },
    '& .rbc-time-content': {
        borderTop: `1px solid ${theme.palette.divider}`,
    },
}));

const platformConfig = {
    whatsapp: { color: '#25D366', icon: <WhatsApp fontSize="small" /> },
    telegram: { color: '#0088cc', icon: <Telegram fontSize="small" /> },
    instagram: { color: '#E4405F', icon: <Instagram fontSize="small" /> },
    facebook: { color: '#1877F2', icon: <Facebook fontSize="small" /> },
};

const ContentCalendar = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [currentView, setCurrentView] = useState('month');
    const [currentDate, setCurrentDate] = useState(new Date());
    const [filterPlatform, setFilterPlatform] = useState('all');

    useEffect(() => {
        fetchPosts();
    }, [currentDate, currentView, filterPlatform]);

    const fetchPosts = async () => {
        try {
            const token = localStorage.getItem('token');
            let start, end;

            if (currentView === 'month') {
                start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
                end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
            } else if (currentView === 'week') {
                start = startOfWeek(currentDate);
                end = new Date(start);
                end.setDate(end.getDate() + 7);
            } else {
                start = new Date(currentDate);
                start.setHours(0, 0, 0, 0);
                end = new Date(currentDate);
                end.setHours(23, 59, 59, 999);
            }

            // Expand range slightly to catch edge cases
            start.setDate(start.getDate() - 7);
            end.setDate(end.getDate() + 7);

            const { data } = await axios.get(`${API_BASE_URL}/api/calendar/posts`, {
                headers: { Authorization: `Bearer ${token}` },
                params: {
                    start: start.toISOString(),
                    end: end.toISOString(),
                    platform: filterPlatform !== 'all' ? filterPlatform : undefined
                },
            });

            // Map data to events
            const formattedEvents = data.map(post => ({
                id: post._id,
                title: post.content?.text || 'Post',
                start: new Date(post.scheduledTime),
                end: new Date(new Date(post.scheduledTime).getTime() + 60 * 60 * 1000), // Default 1 hour
                resource: post,
            }));

            setEvents(formattedEvents);
        } catch (error) {
            console.error('Error fetching calendar posts:', error);
            // Fallback for demo if API fails
            setEvents([]);
        }
    };

    const handleSelectEvent = (event) => {
        setSelectedEvent(event);
        setDialogOpen(true);
    };

    const handleEventDrop = async ({ event, start, end }) => {
        // Optimistic UI update
        const updatedEvents = events.map(e =>
            e.id === event.id ? { ...e, start, end } : e
        );
        setEvents(updatedEvents);

        try {
            const token = localStorage.getItem('token');
            await axios.put(
                `${API_BASE_URL}/api/calendar/move/${event.id}`,
                { newTime: start.toISOString() },
                { headers: { Authorization: `Bearer ${token}` } }
            );
        } catch (error) {
            console.error('Error moving event:', error);
            // alert('Could not move post'); 
            // Silent fail or toast usually better
            fetchPosts(); // Revert
        }
    };

    // Custom Event Component
    const EventComponent = ({ event }) => {
        const platform = event.resource?.platform || 'whatsapp';
        const config = platformConfig[platform];

        return (
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                gap: 0.5,
                overflow: 'hidden'
            }}>
                <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    bgcolor: 'rgba(0,0,0,0.1)',
                    borderRadius: '50%',
                    width: 20,
                    height: 20,
                    minWidth: 20
                }}>
                    {config?.icon}
                </Box>
                <Typography variant="caption" noWrap sx={{ fontWeight: 'bold', fontSize: '0.75rem' }}>
                    {event.title}
                </Typography>
            </Box>
        );
    };

    const eventStyleGetter = (event) => {
        const platform = event.resource?.platform || 'whatsapp';
        const color = platformConfig[platform]?.color || theme.palette.primary.main;

        return {
            style: {
                backgroundColor: color,
                borderLeft: `4px solid ${alpha('#000', 0.2)}`,
                color: 'white',
                borderRadius: '6px',
                padding: '2px 4px',
            }
        };
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
                <Box>
                    <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ color: 'text.primary' }}>
                        تقويم المحتوى
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        خطط وجدول منشوراتك عبر جميع المنصات في مكان واحد
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/scheduled')}
                    sx={{
                        px: 4,
                        py: 1.5,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontSize: '1rem',
                        boxShadow: 4
                    }}
                >
                    منشور جديد
                </Button>
            </Box>

            <CalendarWrapper>
                <Calendar
                    localizer={localizer}
                    events={events}
                    startAccessor="start"
                    endAccessor="end"
                    views={['month', 'week', 'day']}
                    view={currentView}
                    onView={setCurrentView}
                    date={currentDate}
                    onNavigate={setCurrentDate}
                    onSelectEvent={handleSelectEvent}
                    onEventDrop={handleEventDrop}
                    draggableAccessor={() => true}
                    resizable
                    components={{
                        toolbar: (props) => (
                            <CalendarToolbar
                                {...props}
                                filterPlatform={filterPlatform}
                                onFilterChange={setFilterPlatform}
                            />
                        ),
                        event: EventComponent,
                    }}
                    eventPropGetter={eventStyleGetter}
                    messages={{
                        noEventsInRange: 'لا توجد منشورات في هذا النطاق',
                    }}
                    formats={{
                        dateFormat: 'd',
                        dayFormat: (date, culture, localizer) =>
                            localizer.format(date, 'EEEE', culture),
                    }}
                    culture="ar"
                    rtl={true}
                />
            </CalendarWrapper>

            {/* Premium Post Details Dialog */}
            <Dialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        backgroundImage: 'none',
                        boxShadow: 24
                    }
                }}
                TransitionProps={{ mountOnEnter: true, unmountOnExit: true }}
            >
                {selectedEvent && (
                    <>
                        <Box sx={{
                            p: 3,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            bgcolor: alpha(platformConfig[selectedEvent.resource.platform]?.color || theme.palette.primary.main, 0.1)
                        }}>
                            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                <Avatar sx={{
                                    bgcolor: platformConfig[selectedEvent.resource.platform]?.color,
                                    width: 48,
                                    height: 48
                                }}>
                                    {platformConfig[selectedEvent.resource.platform]?.icon}
                                </Avatar>
                                <Box>
                                    <Typography variant="h6" fontWeight="bold">
                                        {selectedEvent.resource.platform.charAt(0).toUpperCase() + selectedEvent.resource.platform.slice(1)} Post
                                    </Typography>
                                    <Chip
                                        icon={selectedEvent.resource?.status === 'published' ? <CheckCircle /> : <Schedule />}
                                        label={selectedEvent.resource?.status === 'published' ? 'تم النشر' : 'مجدول'}
                                        size="small"
                                        color={selectedEvent.resource?.status === 'published' ? 'success' : 'warning'}
                                        sx={{ mt: 0.5, fontWeight: 'bold' }}
                                    />
                                </Box>
                            </Box>
                            <IconButton onClick={() => setDialogOpen(false)} size="small">
                                <Close />
                            </IconButton>
                        </Box>

                        <DialogContent sx={{ p: 0 }}>
                            <Box sx={{ p: 3 }}>
                                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                    محتوى المنشور
                                </Typography>
                                <Card variant="outlined" sx={{ bgcolor: 'background.default', border: 'none' }}>
                                    <CardContent>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>
                                            {selectedEvent.title}
                                        </Typography>
                                    </CardContent>
                                </Card>

                                <Box sx={{ mt: 3, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            التاريخ
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {format(selectedEvent.start, 'dd MMMM yyyy', { locale: ar })}
                                        </Typography>
                                    </Box>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            الوقت
                                        </Typography>
                                        <Typography variant="body2" fontWeight="medium">
                                            {format(selectedEvent.start, 'hh:mm a', { locale: ar })}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, borderTop: '1px solid', borderColor: 'divider', justifyContent: 'space-between' }}>
                            <Button
                                startIcon={<Delete />}
                                color="error"
                                onClick={() => {
                                    alert('Delete functionality to be connected');
                                    setDialogOpen(false);
                                }}
                            >
                                حذف المنشور
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<Edit />}
                                onClick={() => {
                                    navigate(`/scheduled/${selectedEvent.id}/edit`);
                                }}
                                sx={{ px: 4, borderRadius: 2 }}
                            >
                                تعديل المحتوى
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Container>
    );
};

export default ContentCalendar;
